import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Post } from '../model/post.types';
import { useDeletePost } from '../api/useDeletePost';
import { useBlockUser } from '../api/useBlockUser';
import { usePinPostAction } from '../lib/usePinPostAction';
import { useReplyInDm } from '../lib/useReplyInDm';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { ActionConfirmModal } from './ActionConfirmModal';

type ConfirmKind = 'delete' | 'block' | null;

type Props = {
  post: Post;
  pinnedCount: number;
  authorBlocked: boolean;
};

export const OthersPostActionsMenu = ({
  post,
  pinnedCount,
  authorBlocked,
}: Props) => {
  const { communityId, isDmFeatureEnabled } = useCommunityContext();
  const deletePost = useDeletePost();
  const blockUser = useBlockUser();
  const pin = usePinPostAction(post, communityId, pinnedCount);
  const [confirming, setConfirming] = useState<ConfirmKind>(null);

  const { id: authorId, email, canReceiveDm } = post.author;
  const replyInDM = useReplyInDm(authorId);

  const dismiss = useCallback(() => setConfirming(null), []);
  const openDelete = useCallback(() => setConfirming('delete'), []);
  const openBlock = useCallback(() => setConfirming('block'), []);

  const copyEmail = useCallback(() => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    analytics.trackAuthorEmailCopied();
    toast.success('Email copied to clipboard!');
  }, [email]);

  const confirmDelete = useCallback(() => {
    analytics.trackPostDeleted(post);
    deletePost({ communityId, postId: post.id });
    setConfirming(null);
  }, [deletePost, communityId, post]);

  const confirmBlock = useCallback(() => {
    analytics.trackUserBlocked('feed');
    blockUser({ communityId, userId: authorId });
    setConfirming(null);
  }, [blockUser, communityId, authorId]);

  const items = useMemo<ActionMenuItem[]>(() => {
    const entries: Array<ActionMenuItem | null> = [
      {
        key: 'pin',
        label: 'Pin to top',
        disabled: pin.disabled,
        onClick: pin.toggle,
        toggle: { checked: pin.checked, helperText: pin.helperText },
      },
      email
        ? { key: 'copy-email', label: 'Copy email', onClick: copyEmail }
        : null,
      isDmFeatureEnabled && canReceiveDm
        ? { key: 'reply-dm', label: 'Reply in DM', onClick: replyInDM }
        : null,
      {
        key: 'delete',
        label: 'Delete post',
        destructive: true,
        onClick: openDelete,
      },
      authorBlocked
        ? null
        : {
            key: 'block',
            label: 'Block user',
            destructive: true,
            onClick: openBlock,
          },
    ];
    return entries.filter((item): item is ActionMenuItem => item !== null);
  }, [
    pin.disabled,
    pin.toggle,
    pin.checked,
    pin.helperText,
    email,
    isDmFeatureEnabled,
    canReceiveDm,
    authorBlocked,
    copyEmail,
    replyInDM,
    openDelete,
    openBlock,
  ]);

  return (
    <>
      <ActionMenu items={items} />
      <ActionConfirmModal
        open={confirming === 'delete'}
        title="Are you sure you want to delete this post?"
        actionLabel="Delete"
        onConfirm={confirmDelete}
        onDismiss={dismiss}
      />
      <ActionConfirmModal
        open={confirming === 'block'}
        title="Are you sure you want to block this user?"
        body="This means that they will no longer be able to engage in your community."
        actionLabel="Block user"
        onConfirm={confirmBlock}
        onDismiss={dismiss}
      />
    </>
  );
};
